
import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import GameBoard from './components/GameBoard';
import PatternSelector from './components/PatternSelector';
import PatternIcon from './components/PatternIcon';
// import GameInfo from './components/GameInfo';
import AIPlayerSelector from './components/AIPlayerSelector';
import GameModeSelector from './components/GameModeSelector';
import TestMode from './components/TestMode';
import GameGeneration from './components/GameGeneration';
import { checkWin as checkWinPattern, getCompatiblePatterns } from './config/patterns';
import { calculateAIMove, getPlayerId, STRATEGIES } from './config/oracle-ai';
import { getAIPlayerById, fetchAIPlayers } from './config/ai-players-config';
// import StrategySelector from './components/StrategySelector';
import AvatarSelector from './components/AvatarSelector';
import TitleLogo from './components/TitleLogo';
import Tournament from './components/Tournament';
import HeadToHead from './components/HeadToHead';
import TournamentOptimizer from './components/TournamentOptimizer';
import { saveGameSettings, loadGameSettings, testCookieFunctionality } from './utils/cookieManager';
import { initializeGameLogging, logMove, completeGameLogging, startMoveTimer, requestAndAttachLocation } from './utils/gameLogger';

// TODO: Game Line Analysis Feature
// - Implement function to count unique game paths between AI players
// - Analyze branching points and strategic complexity
// - Use to identify "interesting" matchups for tournaments
// - Consider factors: unique paths, win rate variation, game length, strategic depth
// - Potential file: src/utils/gameLineAnalyzer.js
// - COMPUTATIONAL CONSIDERATIONS: 9x8x20 = 1440 combinations, ~20s per game
// - Need optimization strategies: progressive sampling, parallel processing, intelligent filtering
// - Start with pilot study: 3x4x6 = 72 combos, 50 games each = ~20 hours computation
// - MULTI-CORE OPTIMIZATION: Current implementation uses single-threaded concurrent execution
// - Consider Web Workers for true parallel processing across multiple CPU cores
// - Could significantly improve tournament performance for large-scale analysis

function App() {
  // Initialize with default values first
  const [boardSize, setBoardSize] = useState(4);
  const [selectedPattern, setSelectedPattern] = useState('elly');
  const [gameMode, setGameMode] = useState('player-computer');
  const [selectedStrategy, setSelectedStrategy] = useState(STRATEGIES.VONNEUMANN);
  const [selectedAIPlayer1, setSelectedAIPlayer1] = useState('harary');
  const [selectedAIPlayer2, setSelectedAIPlayer2] = useState('harary');
  const [selectedAvatar, setSelectedAvatar] = useState('🍀');
  const [selectedAvatar2, setSelectedAvatar2] = useState('🎲');
  const [showHeatmap, setShowHeatmap] = useState(false);

  
  const [gameState, setGameState] = useState('playing'); // 'playing', 'won', 'draw'
  const [currentPlayer, setCurrentPlayer] = useState('X'); // 'X' for human, 'O' for AI
  const [board, setBoard] = useState([]);
  const [winner, setWinner] = useState(null);
  const [winningPositions, setWinningPositions] = useState([]);
  const [aiThinking, setAiThinking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [hasStarted, setHasStarted] = useState(false); // legacy; button always shows New Game now
  const [moveHistory, setMoveHistory] = useState([]); // {row,col,player}
  const [player1Type, setPlayer1Type] = useState('human'); // 'human' | 'ai'
  const [player2Type, setPlayer2Type] = useState('ai');
  const [historyIndex, setHistoryIndex] = useState(0); // number of moves applied from moveHistory
  const [showTestMode, setShowTestMode] = useState(false);
  const [showGameGeneration, setShowGameGeneration] = useState(false);
  const [showTournament, setShowTournament] = useState(false);
  const [showHeadToHead, setShowHeadToHead] = useState(false);
  const [showTournamentOptimizer, setShowTournamentOptimizer] = useState(false);
  const [showDominoHighlighting, setShowDominoHighlighting] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showPatternSelectorModal, setShowPatternSelectorModal] = useState(false);
  const [showAIQuickModal, setShowAIQuickModal] = useState(false);
  const [availableAIPlayers, setAvailableAIPlayers] = useState([]);
  const aiTimeoutRef = useRef(null);
  const [showResult, setShowResult] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const resultTimerRef = useRef(null);
  const [isDeveloperMode, setIsDeveloperMode] = useState(false);
  const [showMoveEvaluation, setShowMoveEvaluation] = useState(false);
  const [useNashAnalysis, setUseNashAnalysis] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Load saved settings from cookies after component mounts
  useEffect(() => {
    console.log('🚀 App component mounted');
    // Load saved settings
    const savedSettings = loadGameSettings();
    console.log('🔍 Loading saved settings:', savedSettings);
    if (savedSettings) {
      console.log('🔍 Applying saved settings...');
      setBoardSize(savedSettings.boardSize || 4);
      setSelectedPattern(savedSettings.selectedPattern || 'elly');
      setGameMode(savedSettings.gameMode || 'player-computer');
      setSelectedStrategy(savedSettings.selectedStrategy || STRATEGIES.VONNEUMANN);
      // Handle migration from numeric IDs to string IDs
      const player1 = typeof savedSettings.selectedAIPlayer1 === 'number' ? 'harary' : (savedSettings.selectedAIPlayer1 || 'harary');
      const player2 = typeof savedSettings.selectedAIPlayer2 === 'number' ? 'harary' : (savedSettings.selectedAIPlayer2 || 'harary');
      setSelectedAIPlayer1(player1);
      setSelectedAIPlayer2(player2);
      if (savedSettings.player1Type === 'human' || savedSettings.player1Type === 'ai') {
        setPlayer1Type(savedSettings.player1Type);
      }
      if (savedSettings.player2Type === 'human' || savedSettings.player2Type === 'ai') {
        setPlayer2Type(savedSettings.player2Type);
      }
      setSelectedAvatar(savedSettings.selectedAvatar || '🍀');
      setShowHeatmap(!!savedSettings.showHeatmap);
      setShowMoveEvaluation(!!savedSettings.showMoveEvaluation);
      setUseNashAnalysis(!!savedSettings.useNashAnalysis);
      console.log('🔍 Settings applied successfully');
    } else {
      console.log('🔍 No saved settings found, using defaults');
    }
    // Load dynamic AI players from backend
    (async () => {
      const list = await fetchAIPlayers();
      setAvailableAIPlayers(Array.isArray(list) ? list : []);
      // Auto-start on load (initializeBoard effect will set to 'playing')
      setHasStarted(true);
    })();
    // Mark settings as loaded to enable subsequent auto-saves
    setSettingsLoaded(true);
  }, []);

  // Auto-save settings whenever key values change
  useEffect(() => {
    if (!settingsLoaded) return; // avoid overwriting cookie with defaults on first mount
    const settings = {
      boardSize,
      selectedPattern,
      gameMode,
      selectedStrategy,
      selectedAIPlayer1,
      selectedAIPlayer2,
      selectedAvatar,
      showHeatmap,
      showMoveEvaluation,
      useNashAnalysis,
      player1Type,
      player2Type
    };
    saveGameSettings(settings);
  }, [settingsLoaded, boardSize, selectedPattern, gameMode, selectedStrategy, selectedAIPlayer1, selectedAIPlayer2, selectedAvatar, showHeatmap, showMoveEvaluation, useNashAnalysis, player1Type, player2Type]);

  // Function to save all game settings to cookies
  // const saveSettings = () => {
  //   const settings = {
  //     boardSize,
  //     selectedPattern,
  //     gameMode,
  //     selectedStrategy,
  //     selectedAIPlayer1,
  //     selectedAIPlayer2,
  //     selectedAvatar,
  //     showHeatmap
  //   };
  //   saveGameSettings(settings);
  // };

  // Function to clear all saved settings
  const clearSettings = () => {
    // Reset to default values
    setBoardSize(6);
    setSelectedPattern('aspy');
    setGameMode('player-computer');
    setSelectedStrategy(STRATEGIES.GARDNER);
    setSelectedAIPlayer1('alfred');
    setSelectedAIPlayer2('harary');
    setSelectedAvatar('🍀');
    setShowHeatmap(false);
    
    // Clear the cookie
    import('./utils/cookieManager').then(({ clearGameSettings }) => {
      clearGameSettings();
    });
    
    // Reset the game
    resetGame();
  };

  // Initialize board
  useEffect(() => {
    initializeBoard();
  }, [boardSize]);

  // Developer mode keyboard shortcut (Ctrl+Shift+D)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        setIsDeveloperMode(prev => {
          const newMode = !prev;
          console.log(`🔧 Developer mode ${newMode ? 'enabled' : 'disabled'}`);
          return newMode;
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Enforce player selection constraints when developer mode is OFF
  useEffect(() => {
    if (!isDeveloperMode) {
      // Force Player 1 to Human
      if (player1Type !== 'human') {
        setPlayer1Type('human');
      }
      // Force Player 2 to be AI (default Harary) if currently Human
      if (player2Type === 'human') {
        setPlayer2Type('ai');
        setSelectedAIPlayer2('harary');
      }
      // Game mode becomes PvC in non-dev mode
      if (gameMode !== 'player-computer') {
        setGameMode('player-computer');
      }
      // Ensure game not paused and reset so UI reflects constraints immediately
      setIsPaused(false);
      resetGame();
    }
  }, [isDeveloperMode]);

  const initializeBoard = () => {
    const newBoard = Array(boardSize).fill(null).map(() => Array(boardSize).fill(null));
    setBoard(newBoard);
    setGameState('playing');
    setCurrentPlayer('X');
    setWinner(null);
    setWinningPositions([]);
    setMoveHistory([]);
    setHistoryIndex(0);
    if (resultTimerRef.current) { clearTimeout(resultTimerRef.current); resultTimerRef.current = null; }
    setShowResult(false);
    setResultMessage('');
    
    // Initialize game logging
    initializeGameLogging({
      boardSize,
      patternId: selectedPattern,
      gameMode,
      player1Type,
      player2Type,
      player1Key: player1Type === 'ai' ? selectedAIPlayer1 : null,
      player2Key: player2Type === 'ai' ? selectedAIPlayer2 : null,
      player1TimeBudget: player1Type === 'ai' ? getAIPlayerById(selectedAIPlayer1)?.timeBudgetMs : null,
      player2TimeBudget: player2Type === 'ai' ? getAIPlayerById(selectedAIPlayer2)?.timeBudgetMs : null
    });
  };

  const checkWin = (board, pattern, player) => {
    return checkWinPattern(board, pattern, player, boardSize);
  };



  const checkDraw = (board) => {
    return board.every(row => row.every(cell => cell !== null));
  };

  const makeMove = (row, col) => {
    if (gameState !== 'playing' || board[row][col] !== null || aiThinking) {
      return;
    }

    // Only allow human moves in player-player mode or when it's the human's turn in player-computer mode
    if (gameMode === 'computer-computer' || 
        (gameMode === 'player-computer' && currentPlayer === 'O')) {
      return;
    }

    // If user has stepped back in PvC and clicks a square
    if (gameMode === 'player-computer' && historyIndex < moveHistory.length) {
      const expected = moveHistory[historyIndex];
      if (expected && expected.player === currentPlayer && expected.row === row && expected.col === col) {
        // Replay recorded move without truncating future history
        const newBoard = board.map(r => [...r]);
        if (newBoard[row][col] !== null) return;
        newBoard[row][col] = currentPlayer;
        setBoard(newBoard);
        setHistoryIndex(idx => idx + 1);
        const winResult = checkWin(newBoard, selectedPattern, currentPlayer);
        if (winResult) {
          setGameState('won');
          setWinner(currentPlayer);
          setWinningPositions(winResult.positions);
          return;
        }
        if (checkDraw(newBoard)) {
          setGameState('draw');
          return;
        }
        setCurrentPlayer(currentPlayer === 'X' ? 'O' : 'X');
        return;
      } else {
        // Branching to a new timeline: inform and proceed (future history will be truncated in executeMove)
        if (resultTimerRef.current) { clearTimeout(resultTimerRef.current); }
        setResultMessage('New timeline!');
        setShowResult(true);
        resultTimerRef.current = setTimeout(() => setShowResult(false), 2000);
      }
    }

    executeMove(row, col, currentPlayer);
  };

  const makeAIMove = (row, col) => {
    if (gameState !== 'playing' || board[row][col] !== null) {
      return;
    }

    executeMove(row, col, currentPlayer);
  };

  const executeMove = (row, col, player) => {
    const newBoard = board.map(row => [...row]);
    newBoard[row][col] = player;
    setBoard(newBoard);
    // Append to history at the current index (truncate any future moves)
    setMoveHistory(prev => {
      const next = prev.slice(0, historyIndex);
      next.push({ row, col, player });
      return next;
    });
    setHistoryIndex(idx => idx + 1);

    // Check for win
    const winResult = checkWin(newBoard, selectedPattern, player);
    if (winResult) {
      setGameState('won');
      setWinner(player);
      setWinningPositions(winResult.positions);
      
      // Log game completion
      completeGameLogging(player, historyIndex + 1);
      return;
    }

    // Check for draw
    if (checkDraw(newBoard)) {
      setGameState('draw');
      
      // Log game completion
      completeGameLogging('draw', historyIndex + 1);
      return;
    }

    // Switch players
    setCurrentPlayer(player === 'X' ? 'O' : 'X');
  };

  // AI move
  useEffect(() => {
    console.log('🎮 AI move useEffect triggered:', { 
      gameState, 
      isPaused, 
      historyIndex, 
      moveHistoryLength: moveHistory.length,
      gameMode,
      currentPlayer,
      aiThinking
    });
    
    if (gameState !== 'playing') {
      console.log('🎮 AI move skipped - game not playing');
      return;
    }
    if (isPaused) {
      console.log('🎮 AI move skipped - game paused');
      return;
    }
    // When replaying recorded history (stepping through the timeline), do not generate new AI moves
    if (historyIndex < moveHistory.length) {
      console.log('🎮 AI move skipped - replaying history');
      return;
    }
    
    const shouldGenerateAIMove = (gameMode === 'computer-computer') || (gameMode === 'player-computer' && currentPlayer === 'O');
    console.log('🎮 AI move condition check:', { 
      gameMode, 
      currentPlayer, 
      shouldGenerateAIMove,
      computerComputer: gameMode === 'computer-computer',
      playerComputerO: gameMode === 'player-computer' && currentPlayer === 'O'
    });
    
    if (shouldGenerateAIMove) {
      console.log('🎮 AI move condition met:', { gameMode, currentPlayer, aiThinking });
      // Prevent multiple AI moves from being triggered simultaneously
      if (aiThinking) {
        console.log('🎮 AI already thinking, skipping move generation');
        return;
      }
      setAiThinking(true);
      console.log('🎮 Starting AI move generation for:', { gameMode, currentPlayer, historyIndex });
      const timeoutId = setTimeout(async () => {
        let playerId;
        
        // Determine which AI player to use based on current player and game mode
        if (gameMode === 'computer-computer') {
          if (currentPlayer === 'X') {
            playerId = getPlayerId(selectedAIPlayer1);
          } else {
            playerId = getPlayerId(selectedAIPlayer2);
          }
        } else {
          // Player vs Computer mode - AI is always Player 2 (O)
          playerId = getPlayerId(selectedAIPlayer2);
        }
        
        // Determine if AI is the first player (X) or second player (O)
        const isFirstPlayer = (gameMode === 'computer-computer' && currentPlayer === 'X');
        
        // Auto-assign strategy based on AI player's level
        const idForSide = (gameMode === 'computer-computer')
          ? (currentPlayer === 'X' ? selectedAIPlayer1 : selectedAIPlayer2)
          : (currentPlayer === 'O' ? selectedAIPlayer2 : selectedAIPlayer1);
        const cfg = getAIPlayerById(idForSide);
        let strategyToUse = cfg?.aiMethod || STRATEGIES.VONNEUMANN;
        
        try {
          const aiMove = await calculateAIMove(board, selectedPattern, playerId, boardSize, strategyToUse, isFirstPlayer, 0, idForSide);
          console.log('🎮 AI Move Debug:', {
            aiMove,
            playerId,
            strategy: strategyToUse,
            selectedStrategy,
            gameMode,
            currentPlayer,
            isFirstPlayer,
            selectedPlayers: { player1: selectedAIPlayer1, player2: selectedAIPlayer2 },
            pattern: selectedPattern
          });
          if (aiMove) {
            makeAIMove(aiMove.row, aiMove.col);
          } else {
            console.log('No AI move found - this might indicate a problem');
          }
        } catch (error) {
          console.error('🎮 AI move calculation failed:', error);
        }
        setAiThinking(false);
      }, gameMode === 'computer-computer' ? 300 : 500); // Faster for computer-computer mode
      aiTimeoutRef.current = timeoutId;
    }
    return () => {
      if (aiTimeoutRef.current) {
        clearTimeout(aiTimeoutRef.current);
        aiTimeoutRef.current = null;
      }
    };
  }, [currentPlayer, gameState, board, selectedPattern, boardSize, selectedAIPlayer1, selectedAIPlayer2, gameMode, isPaused, historyIndex]);

  // Controls: Play/Pause/Back/Step (visible for all modes; Back/Step operate when paused)
  const handlePlay = () => {
    setIsPaused(false);
  };

  const handlePause = () => {
    setIsPaused(true);
  };

  const handleStepBack = () => {
    // Auto-pause when stepping back only in CvC
    if (gameMode === 'computer-computer' && !isPaused) setIsPaused(true);
    if (historyIndex === 0) return;

    // Cancel any pending AI action and allow stepping
    if (aiTimeoutRef.current) { clearTimeout(aiTimeoutRef.current); aiTimeoutRef.current = null; }
    setAiThinking(false);

    if (gameMode === 'player-computer') {
      // Jump back to just before the previous human ('X') move
      let targetIndex = -1;
      for (let i = historyIndex - 1; i >= 0; i--) {
        if (moveHistory[i]?.player === 'X') { targetIndex = i; break; }
      }
      if (targetIndex < 0) return;

      // Rebuild board up to moves before targetIndex (exclude that human move)
      const fresh = Array(boardSize).fill(null).map(() => Array(boardSize).fill(null));
      for (let j = 0; j < targetIndex; j++) {
        const m = moveHistory[j];
        fresh[m.row][m.col] = m.player;
      }
      setBoard(fresh);
      setHistoryIndex(targetIndex);
      setGameState('playing');
      setWinner(null);
      setWinningPositions([]);
      // Clear any lingering result banner and timer
      if (resultTimerRef.current) { clearTimeout(resultTimerRef.current); resultTimerRef.current = null; }
      setShowResult(false);
      setResultMessage('');
      setCurrentPlayer('X');
      return;
    }

    // Default single-step back (CvC and PvP)
    const last = moveHistory[historyIndex - 1];
    const newBoard = board.map(r => [...r]);
    newBoard[last.row][last.col] = null;
    setBoard(newBoard);
    setHistoryIndex(idx => Math.max(0, idx - 1));
    setGameState('playing');
    setWinner(null);
    setWinningPositions([]);
    // Clear any lingering result banner and timer
    if (resultTimerRef.current) { clearTimeout(resultTimerRef.current); resultTimerRef.current = null; }
    setShowResult(false);
    setResultMessage('');
    setCurrentPlayer(last.player);
  };

  const handleStepForward = () => {
    // Auto-pause when stepping forward only in CvC
    if (gameMode === 'computer-computer' && !isPaused) setIsPaused(true);
    if (historyIndex < moveHistory.length) {
      // Replay recorded move
      const nextMove = moveHistory[historyIndex];
      const newBoard = board.map(r => [...r]);
      if (newBoard[nextMove.row][nextMove.col] !== null) return;
      newBoard[nextMove.row][nextMove.col] = nextMove.player;
      setBoard(newBoard);
      setHistoryIndex(idx => idx + 1);
      const winResult = checkWin(newBoard, selectedPattern, nextMove.player);
      if (winResult) {
        setGameState('won');
        setWinner(nextMove.player);
        setWinningPositions(winResult.positions);
        return;
      }
      if (checkDraw(newBoard)) {
        setGameState('draw');
        return;
      }
      setCurrentPlayer(nextMove.player === 'X' ? 'O' : 'X');
    } else {
      // At end of timeline: compute and apply one AI move for current side if that side is AI
      const canGenerateAIMove = (gameMode === 'computer-computer') || (gameMode === 'player-computer' && currentPlayer === 'O');
      if (!canGenerateAIMove) return;
      (async () => {
        try {
          setAiThinking(true);
          let playerId = currentPlayer === 'X' ? getPlayerId(selectedAIPlayer1) : getPlayerId(selectedAIPlayer2);
          const isFirstPlayer = currentPlayer === 'X';
          const idForSide = currentPlayer === 'X' ? selectedAIPlayer1 : selectedAIPlayer2;
          const cfg = getAIPlayerById(idForSide);
          let strategyToUse = cfg?.aiMethod || STRATEGIES.VONNEUMANN;
          // Assign ThreeSpace to Paul Erdos
          if (cfg && cfg.id === 'erdos') {
            strategyToUse = STRATEGIES.THREESPACE;
          }
          const aiMove = await calculateAIMove(board, selectedPattern, playerId, boardSize, strategyToUse, isFirstPlayer, 0, idForSide);
          if (aiMove && board[aiMove.row][aiMove.col] === null && gameState === 'playing') {
            makeAIMove(aiMove.row, aiMove.col);
          }
        } catch (_) {
        } finally {
          setAiThinking(false);
        }
      })();
    }
  };

  const resetGame = () => {
    console.log('🎮 Resetting game, clearing AI state');
    if (aiTimeoutRef.current) {
      clearTimeout(aiTimeoutRef.current);
      aiTimeoutRef.current = null;
    }
    setAiThinking(false);
    initializeBoard();
    setGameState('playing');
  };

  // Start or New Game action: if never started, start; otherwise abort current and start fresh
  const handleStartOrNewGame = () => {
    if (!hasStarted) {
      setHasStarted(true);
    }
    // In CvC, always start running immediately
    if (gameMode === 'computer-computer') {
      setIsPaused(false);
    }
    resetGame();
  };

  // Human PASS: only valid at game start in PvC when human (X) is to move
  const handlePass = () => {
    if (gameMode !== 'player-computer') return;
    if (currentPlayer !== 'X') return;
    if (moveHistory.length !== 0) return;
    if (!hasStarted) { setHasStarted(true); }
    if (gameState !== 'playing') { setGameState('playing'); }
    
    // Ensure historyIndex is at the end of the timeline to trigger AI move
    setHistoryIndex(0);
    setCurrentPlayer('O');
    
    // Ensure game is not paused in PvC mode
    setIsPaused(false);
    
    console.log('🎮 PASS clicked - switching to AI turn:', { 
      gameMode, 
      currentPlayer: 'O', 
      historyIndex: 0, 
      moveHistoryLength: moveHistory.length,
      isPaused: false
    });
  };

  // Active AI (used to show avatar/name in the board controls box)
  const activeAIId = gameMode === 'computer-computer'
    ? (currentPlayer === 'X' ? selectedAIPlayer1 : selectedAIPlayer2)
    : (currentPlayer === 'O' ? selectedAIPlayer2 : selectedAIPlayer1);
  const activeAI = getAIPlayerById(activeAIId);
  // Determine when Step Forward should be enabled
  const canStepForward = (() => {
    if (aiThinking) return false;
    // If there is recorded future, allow stepping regardless of mode/turn
    if (historyIndex < moveHistory.length) return true;
    // At end of timeline: only allow when we would generate a new move
    if (gameMode === 'computer-computer') return isPaused && gameState === 'playing';
    if (gameMode === 'player-computer') return currentPlayer === 'O' && gameState === 'playing';
    // player-player: no generation beyond recorded history
    return false;
  })();
  // Primary CTA label/behavior: Play only when paused mid-game; otherwise Start/New Game
  // Primary CTA always shows New Game; use Play/Pause controls for resume in CvC
  const atEndOfTimeline = historyIndex >= moveHistory.length;
  const getFirstName = (fullName) => {
    if (!fullName || typeof fullName !== 'string') return '';
    const parts = fullName.trim().split(' ');
    return parts[0] || fullName;
  };



  // Show transient result banner on win/draw
  useEffect(() => {
    if (gameState === 'won' || gameState === 'draw') {
      let msg = '';
      if (gameState === 'draw') {
        msg = 'Game is a draw!';
      } else if (gameState === 'won') {
        if (gameMode === 'player-computer') {
          if (winner === 'X') msg = 'You win!';
          else {
            const cfg = getAIPlayerById(selectedAIPlayer2);
            const name = (cfg && cfg.name) ? getFirstName(cfg.name) : 'AI';
            msg = `${name} wins!`;
          }
        } else if (gameMode === 'computer-computer') {
          const cfg = getAIPlayerById(winner === 'X' ? selectedAIPlayer1 : selectedAIPlayer2);
          const name = (cfg && cfg.name) ? getFirstName(cfg.name) : 'AI';
          msg = `${name} wins!`;
        } else {
          msg = `${winner} wins!`;
        }
      }
      setResultMessage(msg);
      setShowResult(true);
      if (resultTimerRef.current) { clearTimeout(resultTimerRef.current); }
      resultTimerRef.current = setTimeout(() => { setShowResult(false); }, 2000);
    }
    return () => {
      if (resultTimerRef.current && (gameState === 'won' || gameState === 'draw')) {
        clearTimeout(resultTimerRef.current);
        resultTimerRef.current = null;
      }
    };
  }, [gameState, winner, gameMode, selectedAIPlayer1, selectedAIPlayer2]);

  if (showTestMode) {
    return (
      <div className="App">
        <TestMode onBackToGame={() => setShowTestMode(false)} />
      </div>
    );
  }

  if (showGameGeneration) {
    return (
      <div className="App">
        <GameGeneration onBackToGame={() => setShowGameGeneration(false)} />
      </div>
    );
  }

  if (showTournamentOptimizer) {
    return (
      <div className="App">
        <TournamentOptimizer onBackToGame={() => setShowTournamentOptimizer(false)} />
      </div>
    );
  }

  return (
    <div className="App">
      <div className="game-wrapper">
        <TitleLogo />
        
        {/* Hamburger Menu Button */}
        <div className="hamburger-menu" onClick={() => setShowSettingsModal(true)}>
          <div className="hamburger-icon">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
        
        {/* Developer Mode Indicator */}
        {isDeveloperMode && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: '80px',
            background: 'rgba(76, 175, 80, 0.9)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '0.7rem',
            fontWeight: '600',
            zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            🔧 DEV
          </div>
        )}

        {/* Settings Modal */}
        {showSettingsModal && !showTournament && (
          <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
            <div className="modal-content settings-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Game Settings</h2>
                <button className="modal-close" onClick={() => setShowSettingsModal(false)}>×</button>
              </div>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Essential features - always shown */}
                <div className="board-size-control" style={{ marginBottom: 0 }}>
                  <label>Board Size: </label>
                  <select 
                    value={boardSize} 
                    onChange={(e) => {
                      const newSize = parseInt(e.target.value);
                      setBoardSize(newSize);
                      const compatiblePatterns = getCompatiblePatterns(newSize);
                      if (!compatiblePatterns.some(p => p.id === selectedPattern)) {
                        setSelectedPattern(compatiblePatterns[0]?.id || 'boxy');
                      }
                      resetGame();
                    }}
                    disabled={false}
                  >
                    <option value={3}>3x3</option>
                    <option value={4}>4x4</option>
                    <option value={5}>5x5</option>
                    <option value={6}>6x6</option>
                    <option value={7}>7x7</option>
                    <option value={8}>8x8</option>
                    <option value={9}>9x9</option>
                    <option value={10}>10x10</option>
                  </select>
                </div>
                

                
                <button 
                  onClick={() => window.open('/README.html', '_blank')} 
                  className="readme-button"
                  style={{
                    background: 'linear-gradient(45deg, #4CAF50, #45a049)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '25px',
                    padding: '12px 24px',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)',
                    marginTop: '0'
                  }}
                >
                  📖 View README
                </button>

                {/* Donation button hidden
                <button 
                  onClick={() => window.open('/support.html', '_blank')} 
                  className="support-button"
                  style={{
                    background: 'linear-gradient(45deg, #f59e0b, #d97706)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '25px',
                    padding: '12px 24px',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(217, 119, 6, 0.3)'
                  }}
                >
                  ❤️ Support this project
                </button>
                */}

                <button
                  onClick={async () => {
                    const res = await requestAndAttachLocation();
                    if (res.ok) {
                      alert(`Location enabled. Accuracy ~${Math.round(res.accuracy)}m.`);
                    } else if (res.reason === 'unsupported') {
                      alert('Geolocation not supported on this device/browser.');
                    } else {
                      alert('Could not get location. You may need to allow Location in browser settings.');
                    }
                  }}
                  className="enable-location-button"
                  style={{
                    background: 'linear-gradient(45deg, #06b6d4, #0ea5e9)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '25px',
                    padding: '12px 24px',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(14, 165, 233, 0.3)'
                  }}
                >
                  📍 Enable Precise Location (Optional)
                </button>

                {/* Developer features - only shown in developer mode */}
                {isDeveloperMode && (
                  <>
                    {/* Move Evaluation Checkbox */}
                    <div className="developer-checkbox">
                      <label>
                        <input 
                          type="checkbox"
                          checked={showMoveEvaluation}
                          onChange={(e) => setShowMoveEvaluation(e.target.checked)}
                        />
                        Move Evaluation
                      </label>
                    </div>
                    
                    {/* Nash Equilibrium Color Overlay Checkbox */}
                    <div className="developer-checkbox">
                      <label>
                        <input 
                          type="checkbox"
                          checked={useNashAnalysis}
                          onChange={(e) => setUseNashAnalysis(e.target.checked)}
                        />
                        Nash Equilibrium Color Overlay
                      </label>
                    </div>
                    
                    {historyIndex > 0 && (
                      <button onClick={handleStartOrNewGame} className="reset-button">New Game</button>
                    )}
                    
                    <button onClick={() => setShowTestMode(true)} className="test-mode-button">
                      Test Mode
                    </button>
                    
                    <button onClick={() => setShowGameGeneration(true)} className="game-generation-button">
                      Game Generation
                    </button>

                    <button onClick={() => setShowTournament(true)} className="game-generation-button">
                      Run Tournament (ELO)
                    </button>
                    <button onClick={() => setShowHeadToHead(true)} className="game-generation-button">
                      Head-to-Head
                    </button>
                    
                    <button onClick={() => setShowTournamentOptimizer(true)} className="game-generation-button">
                      Tournament Optimizer
                    </button>

                    <button onClick={clearSettings} className="clear-settings-button">
                      Clear Settings
                    </button>


                    
                    <button onClick={() => {
                      console.log('🧪 Manual cookie test');
                      testCookieFunctionality();
                      console.log('🍪 Current game settings:', {
                        boardSize,
                        selectedPattern,
                        gameMode,
                        selectedStrategy,
                        selectedAIPlayer1,
                        selectedAIPlayer2,
                        selectedAvatar
                      });
                    }} className="test-cookies-button">
                      Test Cookies
                    </button>

                            <button
          onClick={() => window.open('/AWS_DEPLOYMENT_PLAN.html', '_blank')}
          className="deployment-doc-button"
                      style={{
                        background: 'linear-gradient(45deg, #9C27B0, #673AB7)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '25px',
                        padding: '12px 24px',
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 15px rgba(156, 39, 176, 0.3)',
                        marginTop: '8px'
                      }}
                    >
                      🚀 AWS Deployment Plan
                    </button>
                    <button onClick={() => window.open('/docs/index.html', '_blank')} className="game-generation-button">
                      Docs
                    </button>
                    <button onClick={() => window.open('/tournament-stats.html', '_blank')} className="tournament-stats-button">
                      🏆 Tournament Stats
                    </button>
                    <button onClick={() => window.open('/web-analytics.html', '_blank')} className="web-analytics-button">
                      📊 Web Analytics
                    </button>
                  </>
                )}


              </div>
            </div>
          </div>
        )}

        {showSettingsModal && showTournament && (
          <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <Tournament onBack={() => setShowTournament(false)} />
            </div>
          </div>
        )}

        {showSettingsModal && showHeadToHead && (
          <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <HeadToHead onBack={() => setShowHeadToHead(false)} />
            </div>
          </div>
        )}

        {/* Pattern Selector Modal */}
        {showPatternSelectorModal && (
          <div className="modal-overlay" onClick={() => setShowPatternSelectorModal(false)}>
            <div className="modal-content pattern-selector-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Select Target Animal</h2>
                <button className="modal-close" onClick={() => setShowPatternSelectorModal(false)}>×</button>
              </div>
              <div className="modal-body pattern-selector-body">
                <PatternSelector 
                  selectedPattern={selectedPattern}
                  onPatternChange={(newPattern) => {
                    setSelectedPattern(newPattern);
                    resetGame();
                    setShowPatternSelectorModal(false);
                    // saveSettings();
                  }}
                  disabled={false}
                  boardSize={boardSize}
                />
              </div>
            </div>
          </div>
        )}

        {/* Player setup quick modal (opens when clicking the avatar in controls) */}
        {showAIQuickModal && (
          <div className="modal-overlay" onClick={() => { setShowAIQuickModal(false); if (gameMode === 'computer-computer') setIsPaused(true); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Select players</h2>
                <button className="modal-close" onClick={() => { setShowAIQuickModal(false); if (gameMode === 'computer-computer') setIsPaused(true); }}>×</button>
              </div>
              <div className="modal-body">
                {(() => {
                  const options = [{ id: 'human', name: 'Human Player', description: '' }, ...(availableAIPlayers || [])];
                  const recomputeMode = (p1, p2) => {
                    if (p1 === 'ai' && p2 === 'ai') return 'computer-computer';
                    if (p1 === 'human' && p2 === 'human') return 'player-player';
                    return 'player-computer';
                  };
                  return (
                    <div className="player-selectors-grid">
                      <div className="player-selector-col" style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                        <label style={{ fontWeight: 600 }}>Player 1</label>
                        <select
                          className="player-selector-dropdown"
                          style={{ width: '100%', boxSizing: 'border-box' }}
                          value={isDeveloperMode ? (player1Type === 'human' ? 'human' : selectedAIPlayer1) : 'human'}
                          disabled={!isDeveloperMode}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === 'human') {
                              setPlayer1Type('human');
                              const mode = recomputeMode('human', player2Type);
                              setGameMode(mode);
                              if (mode === 'computer-computer') setIsPaused(true);
                            } else {
                              setPlayer1Type('ai');
                              setSelectedAIPlayer1(val);
                              // If Player 2 is currently Human, automatically change to Harary
                              if (player2Type === 'human') {
                                setPlayer2Type('ai');
                                setSelectedAIPlayer2('harary');
                                const mode = recomputeMode('ai', 'ai');
                                setGameMode(mode);
                                if (mode === 'computer-computer') setIsPaused(true);
                              } else {
                                const mode = recomputeMode('ai', player2Type);
                                setGameMode(mode);
                                if (mode === 'computer-computer') setIsPaused(true);
                              }
                            }
                            // Clear any pending AI operations when changing player configuration
                            if (aiTimeoutRef.current) {
                              clearTimeout(aiTimeoutRef.current);
                              aiTimeoutRef.current = null;
                            }
                            setAiThinking(false);
                            resetGame();
                          }}
                        >
                          {options.map(opt => (
                            <option key={opt.id} value={opt.id}>
                              {opt.id === 'human' ? opt.name : `${opt.name} — ${opt.description}`}
                            </option>
                          ))}
                        </select>
                        {player1Type === 'human' && (
                          <div style={{ marginTop: 8 }}>
                            <AvatarSelector
                              selectedAvatar={selectedAvatar}
                              onAvatarChange={(av) => setSelectedAvatar(av)}
                            />
                          </div>
                        )}
                        {player1Type === 'ai' && (
                          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center', width: '100%' }}>
                            <img
                              src={`${(typeof window !== 'undefined' && window.location && window.location.port === '3000') ? 'http://localhost:8000/' : '/'}ai_players/${getAIPlayerById(selectedAIPlayer1)?.iconFile || 'harary.png'}`}
                              alt={getAIPlayerById(selectedAIPlayer1)?.name || 'AI Player'}
                              style={{ width: '100%', height: 'auto', aspectRatio: '1', borderRadius: 8, objectFit: 'cover', border: '2px solid rgba(255,255,255,0.3)' }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="player-selector-col" style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                        <label style={{ fontWeight: 600 }}>Player 2</label>
                        <select
                          className="player-selector-dropdown"
                          style={{ width: '100%', boxSizing: 'border-box' }}
                          value={player2Type === 'human' ? 'human' : selectedAIPlayer2}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === 'human') {
                              if (!isDeveloperMode) {
                                // Disallow selecting Human for Player 2 outside dev mode
                                return;
                              }
                              setPlayer2Type('human');
                              const mode = recomputeMode(player1Type, 'human');
                              setGameMode(mode);
                              if (mode === 'computer-computer') setIsPaused(true);
                            } else {
                              setPlayer2Type('ai');
                              setSelectedAIPlayer2(val);
                              const mode = recomputeMode(player1Type, 'ai');
                              setGameMode(mode);
                              if (mode === 'computer-computer') setIsPaused(true);
                            }
                            // Clear any pending AI operations when changing player configuration
                            if (aiTimeoutRef.current) {
                              clearTimeout(aiTimeoutRef.current);
                              aiTimeoutRef.current = null;
                            }
                            setAiThinking(false);
                            resetGame();
                          }}
                        >
                          {(
                            isDeveloperMode
                              ? options.filter(opt => player1Type !== 'ai' || opt.id !== 'human')
                              : options.filter(opt => opt.id !== 'human')
                           ).map(opt => (
                            <option key={opt.id} value={opt.id}>
                              {opt.id === 'human' ? opt.name : `${opt.name} — ${opt.description}`}
                            </option>
                          ))}
                        </select>
                        {player2Type === 'human' && (
                          <div style={{ marginTop: 8 }}>
                            <AvatarSelector
                              selectedAvatar={selectedAvatar2}
                              onAvatarChange={(av) => setSelectedAvatar2(av)}
                            />
                          </div>
                        )}
                        {player2Type === 'ai' && (
                          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center', width: '100%' }}>
                            <img
                              src={`${(typeof window !== 'undefined' && window.location && window.location.port === '3000') ? 'http://localhost:8000/' : '/'}ai_players/${getAIPlayerById(selectedAIPlayer2)?.iconFile || 'harary.png'}`}
                              alt={getAIPlayerById(selectedAIPlayer2)?.name || 'AI Player'}
                              style={{ width: '100%', height: 'auto', aspectRatio: '1', borderRadius: 8, objectFit: 'cover', border: '2px solid rgba(255,255,255,0.3)' }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
        
        <div className="game-container">
          <div className="game-board-section">
            <div className="board-area">
              <GameBoard 
                board={board}
                onCellClick={makeMove}
                boardSize={boardSize}
                aiThinking={aiThinking}
                winningPositions={winningPositions}
                selectedAIPlayer1={selectedAIPlayer1}
                selectedAIPlayer2={selectedAIPlayer2}
                gameMode={gameMode}
                winningPattern={selectedPattern}
                showDominoHighlighting={showDominoHighlighting}
                selectedAvatar={selectedAvatar}
                selectedAvatar2={selectedAvatar2}
                player2Type={player2Type}
                selectedPattern={selectedPattern}
                showHeatmap={showHeatmap}
                showMoveEvaluation={showMoveEvaluation}
                useNashAnalysis={useNashAnalysis}
              />
              {showResult && (
                <div className="result-overlay" role="status" aria-live="polite">
                  {resultMessage}
                </div>
              )}
            </div>
            <div className="board-controls">
              {historyIndex > 0 && (
                <button onClick={handleStartOrNewGame} className="reset-button">New Game</button>
              )}
              {/* Board Size moved to Settings modal */}
              <div className="current-pattern-display" onClick={() => setShowPatternSelectorModal(true)} title="Current target" aria-label="Current target">
                <div className="pattern-name-display">
                  {getCompatiblePatterns(boardSize).find(p => p.id === selectedPattern)?.name || 'Unknown'}
                </div>
                <div className="pattern-target-label">
                  {`Target animal: ${getCompatiblePatterns(boardSize).find(p => p.id === selectedPattern)?.name || 'Unknown'}`}
                </div>
                <PatternIcon patternId={selectedPattern} size="micro" />
              </div>
              {(gameMode === 'computer-computer' || gameMode === 'player-computer' || gameMode === 'player-player') && (
                <div className="tape-controls">
                    {gameMode === 'computer-computer' && (
                      isPaused ? (
                        <button
                          onClick={handlePlay}
                          className="play-button"
                          disabled={gameState !== 'playing'}
                          title="Play (CvC)"
                          aria-label="Play (CvC)"
                        >
                          ▶
                        </button>
                      ) : (
                        <button
                          onClick={handlePause}
                          className="pause-button"
                          disabled={gameState !== 'playing'}
                          title="Pause (CvC)"
                          aria-label="Pause (CvC)"
                        >
                          ||
                        </button>
                      )
                    )}
                    <button
                      onClick={handleStepBack}
                      className="step-back-button"
                      disabled={historyIndex === 0}
                      title="Back one move"
                      aria-label="Back one move"
                    >
                      ◀
                    </button>
                    <button
                      onClick={handleStepForward}
                      className="step-forward-button"
                      disabled={!canStepForward}
                      title={atEndOfTimeline ? 'At end of timeline' : 'Step forward'}
                      aria-label="Step forward"
                    >
                      ▶
                    </button>
                </div>
              )}
              {gameMode === 'player-computer' ? (
                currentPlayer === 'O' ? (
                  activeAI && (
                    <div onClick={() => setShowAIQuickModal(true)} title="Select players" aria-label="Select players" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.9)', borderRadius: 6, padding: 6, border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer' }}>
                      <div className={`status-label${(gameState === 'won' || gameState === 'draw') ? ' final' : ''}`}>
                        {gameState === 'won' ? `${getFirstName(activeAI.name)} wins!` : (gameState === 'draw' ? 'Draw game!' : `${getFirstName(activeAI.name)}'s turn`)}
                      </div>
                      {(gameState !== 'won' && gameState !== 'draw') && (
                        <img
                          src={`${(typeof window !== 'undefined' && window.location && window.location.port === '3000') ? 'http://localhost:8000/' : '/'}ai_players/${activeAI.iconFile || activeAI.thumbnail}`}
                          alt={activeAI.name}
                          className={`ai-thumb ${aiThinking ? 'jiggle' : ''}`}
                        />
                      )}
                    </div>
                  )
                ) : (
                  <div onClick={() => setShowAIQuickModal(true)} title="Select players" aria-label="Select players" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.9)', borderRadius: 6, padding: 6, border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer' }}>
                    <div className={`status-label${(gameState === 'won' || gameState === 'draw') ? ' final' : ''}`}>
                      {gameState === 'won' ? 'You win!' : (gameState === 'draw' ? 'Draw game!' : 'Your turn')}
                    </div>
                    {(gameState !== 'won' && gameState !== 'draw') && (
                      <div className="human-thumb" style={{ width: 40, height: 40, borderRadius: 6, fontSize: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white' }}>
                        <span role="img" aria-label="player avatar">{selectedAvatar}</span>
                      </div>
                    )}
                  </div>
                )
              ) : gameMode === 'player-player' ? (
                <div onClick={() => setShowAIQuickModal(true)} title="Select players" aria-label="Select players" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.9)', borderRadius: 6, padding: 6, border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer' }}>
                  <div className={`status-label${(gameState === 'won' || gameState === 'draw') ? ' final' : ''}`}>
                    {gameState === 'won' 
                      ? (winner === 'X' ? 'Player 1 wins!' : 'Player 2 wins!')
                      : (gameState === 'draw' 
                          ? 'Draw game!' 
                          : (currentPlayer === 'X' ? 'Player 1\'s turn' : 'Player 2\'s turn')
                        )}
                  </div>
                  {(gameState !== 'won' && gameState !== 'draw') && (
                    currentPlayer === 'X' ? (
                      <div className="human-thumb" style={{ width: 40, height: 40, borderRadius: 6, fontSize: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white' }}>
                        <span role="img" aria-label="player avatar">{selectedAvatar}</span>
                      </div>
                    ) : (
                      (() => {
                        if (player2Type === 'human') {
                          return (
                            <div className="human-thumb" style={{ width: 40, height: 40, borderRadius: 6, fontSize: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white' }}>
                              <span role="img" aria-label="player avatar">{selectedAvatar2}</span>
                            </div>
                          );
                        } else {
                          const player2AI = getAIPlayerById(selectedAIPlayer2);
                          return player2AI ? (
                            <img
                              src={`${(typeof window !== 'undefined' && window.location && window.location.port === '3000') ? 'http://localhost:8000/' : '/'}ai_players/${player2AI.iconFile || player2AI.thumbnail}`}
                              alt={player2AI.name}
                              className="ai-thumb"
                              style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover' }}
                            />
                          ) : (
                            <div className="human-thumb" style={{ width: 40, height: 40, borderRadius: 6, fontSize: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white' }}>
                              <span role="img" aria-label="player avatar">O</span>
                            </div>
                          );
                        }
                      })()
                    )
                  )}
                </div>
              ) : (
                activeAI && (
                  <div onClick={() => setShowAIQuickModal(true)} title="Select players" aria-label="Select players" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.9)', borderRadius: 6, padding: 6, border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer' }}>
                    <div className={`status-label${(gameState === 'won' || gameState === 'draw') ? ' final' : ''}`}>
                      {gameState === 'won'
                        ? (() => { const cfg = getAIPlayerById(winner === 'X' ? selectedAIPlayer1 : selectedAIPlayer2); return `${getFirstName(cfg?.name || 'AI')} wins!`; })()
                        : (gameState === 'draw'
                            ? 'Draw game!'
                            : `${getFirstName(activeAI.name)}'s turn`
                          )}
                    </div>
                    {(gameState !== 'won' && gameState !== 'draw') && (
                      <img
                        src={`${(typeof window !== 'undefined' && window.location && window.location.port === '3000') ? 'http://localhost:8000/' : '/'}ai_players/${activeAI.iconFile || activeAI.thumbnail}`}
                        alt={activeAI.name}
                        className={`ai-thumb ${aiThinking ? 'jiggle' : ''}`}
                      />
                    )}
                  </div>
                )
              )}

              {false && (
                <div className="domino-toggle">
                  <label>
                    <input
                      type="checkbox"
                      checked={showDominoHighlighting}
                      onChange={(e) => setShowDominoHighlighting(e.target.checked)}
                    />
                    Show Domino Pattern
                  </label>
                </div>
              )}
              {false && (
                <div className="heatmap-toggle">
                  <label>
                    <input
                      type="checkbox"
                      checked={showHeatmap}
                      onChange={(e) => setShowHeatmap(e.target.checked)}
                    />
                    Show Pattern Heatmap
                  </label>
                </div>
              )}
              {gameMode === 'player-computer' && currentPlayer === 'X' && moveHistory.length === 0 && (
                <button className="pass-button" onClick={handlePass} title="Let the AI start">PASS</button>
              )}
            </div>

          </div>


        </div>
      </div>
    </div>
  );
}

export default App;
